import React, { useState, useEffect, useRef } from "react";
import InputField from "../InputField";
import SubmitButton from "../SubmitButton";

export default function CustomerRegisterForm({ onRegister, onRequestOTP, isLoading }) {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");

  const [otpSent, setOtpSent] = useState(false);
  const [localError, setLocalError] = useState("");
  const [timer, setTimer] = useState(120);
  const [canResend, setCanResend] = useState(false);
  const intervalRef = useRef(null);

  // مدیریت شمارش معکوس ارسال مجدد کد تایید
  useEffect(() => {
    if (otpSent && timer > 0) {
      intervalRef.current = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
  }, [otpSent, timer]);

  // درخواست ارسال یا ارسال مجدد کد OTP بدون تغییر صفحه
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLocalError("");

    if (!phoneNumber || phoneNumber.length !== 11 || !phoneNumber.startsWith("09")) {
      setLocalError("لطفاً یک شماره موبایل معتبر ۱۱ رقمی (شروع با ۰۹) وارد کنید.");
      return;
    }

    try {
      await onRequestOTP(phoneNumber, "register");
      setOtpSent(true);
      setTimer(120);
      setCanResend(false);
    } catch (err) {
      setLocalError(err.response?.data?.detail || "خطا در ارسال کد تایید. مجدداً تلاش کنید.");
    }
  };

  // ارسال نهایی کل فرم ثبت‌نام
  const handleFinalSubmit = async (e) => {
    e.preventDefault();
    setLocalError("");

    if (!fullName.trim()) {
      setLocalError("لطفاً نام و نام خانوادگی خود را وارد کنید.");
      return;
    }
    if (!phoneNumber || phoneNumber.length !== 11) {
      setLocalError("لطفاً شماره موبایل معتبر وارد کنید.");
      return;
    }
    if (password.length < 8) {
      setLocalError("رمز عبور باید حداقل ۸ کاراکتر باشد.");
      return;
    }
    if (password !== passwordConfirm) {
      setLocalError("کلمه عبور با تکرار آن مطابقت ندارد.");
      return;
    }
    if (!otpSent) {
      setLocalError("لطفاً ابتدا کد تایید را درخواست و وارد کنید.");
      return;
    }
    if (!otp || otp.length < 4) {
      setLocalError("لطفاً کد تایید پیامک شده را وارد کنید.");
      return;
    }

    try {
      await onRegister({
        phoneNumber,
        fullName,
        password,
        passwordConfirm,
        otp,
      });
    } catch (err) {
      setLocalError(err.response?.data?.detail || "ثبت نام ناموفق بود. لطفاً اطلاعات را بررسی کنید.");
    }
  };

  return (
    <form onSubmit={handleFinalSubmit} className="flex flex-col gap-4">
      {/* فیلد نام و نام خانوادگی */}
      <InputField
        label="نام و نام خانوادگی"
        name="fullName"
        type="text"
        placeholder="نام خود را به فارسی وارد کنید"
        value={fullName}
        onChange={(e) => setFullName(e.target.value)}
      />

      {/* فیلدهای رمز عبور */}
      <InputField
        label="رمز عبور"
        name="password"
        type="password"
        placeholder="حداقل ۸ کاراکتر"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
      />

      <InputField
        label="تکرار رمز عبور"
        name="passwordConfirm"
        type="password"
        placeholder="تکرار رمز عبور را وارد کنید"
        value={passwordConfirm}
        onChange={(e) => setPasswordConfirm(e.target.value)}
      />

      {/* بخش شماره موبایل و دکمه دریافت کد در کنار هم */}
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium text-gray-700">شماره موبایل</label>
        <div className="flex gap-2">
          <div className="flex-1">
            <input
              type="text"
              placeholder="مثال: 09123456789"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-left"
              dir="ltr"
            />
          </div>
          <button
            type="button"
            onClick={handleRequestOTP}
            disabled={otpSent && !canResend}
            className={`px-4 py-2 text-sm font-semibold rounded-md border transition whitespace-nowrap ${
              otpSent && !canResend
                ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                : "bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100"
            }`}
          >
            {otpSent ? (canResend ? "ارسال مجدد" : "ارسال شد") : "ارسال کد"}
          </button>
        </div>
      </div>

      {/* فیلد دریافت کد OTP در صورت ارسال موفق */}
      {otpSent && (
        <div className="bg-blue-50/50 p-3 rounded-md border border-blue-100 flex flex-col gap-2">
          <InputField
            label="کد تایید پیامک شده"
            name="otp"
            type="text"
            placeholder="کد ۶ رقمی"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
          />
          <div className="text-xs text-gray-500 pr-1">
            {!canResend && (
              <span>
                ارسال مجدد کد تا {Math.floor(timer / 60)}:
                {String(timer % 60).padStart(2, "0")} دیگر
              </span>
            )}
          </div>
        </div>
      )}



      {localError && <p className="text-red-600 text-sm pr-1">{localError}</p>}

      {/* دکمه ثبت نهایی فرم */}
      <SubmitButton text={isLoading ? "در حال ثبت‌نام..." : "تکمیل ثبت‌نام"} />
    </form>
  );
}
